import { Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-premium';
import { BrandCampaignItem } from '@retail/campaign/types';
import dayjs from 'dayjs';
import React from 'react';
import { useCampaignTFunction } from '@retail/campaign/i18n';
import { baseColumnProps } from './campaignItemsTableBaseColumns';
import { BooleanChip, BooleanOptionsMenu } from '@retail/components';

interface Props {
  handleUpdateIsSalesPriceOverridable?: (args: {
    itemId: string;
    isSalesPriceOverridable: boolean;
  }) => void;
}

export const useGetBrandCampaignItemsColumns = ({ handleUpdateIsSalesPriceOverridable }: Props) => {
  const t = useCampaignTFunction();

  const isImposedColumn: GridColDef<BrandCampaignItem> = {
    ...baseColumnProps,
    field: 'isImposed',
    headerName: t('campaign.columns.imposedCampaignPrice'),
    headerAlign: 'center',
    align: 'center',
    width: 180,
    renderCell: ({ row }) =>
      handleUpdateIsSalesPriceOverridable ? (
        <BooleanOptionsMenu
          value={!row.isSalesPriceOverridable}
          labelTrue={t('campaign.columns.mandatory')}
          labelFalse={t('campaign.columns.notMandatory')}
          onEdit={(value) =>
            handleUpdateIsSalesPriceOverridable({ itemId: row.id, isSalesPriceOverridable: value })
          }
        />
      ) : (
        <BooleanChip
          value={row.isSalesPriceOverridable}
          label={
            row.isSalesPriceOverridable
              ? t('campaign.columns.notMandatory')
              : t('campaign.columns.mandatory')
          }
        />
      ),
    valueGetter: (_, row) => row.isSalesPriceOverridable,
  };

  const lastUpdatedColumn: GridColDef<BrandCampaignItem> = {
    ...baseColumnProps,
    width: 140,
    field: 'lastUpdated',
    headerName: t('campaign.columns.lastUpdated'),
    renderCell: ({ row }) => (
      <Typography variant="subtitle2" component="span">
        {row.updatedAt ? dayjs(row.updatedAt).format('ll') : '-'}
      </Typography>
    ),
  };

  const lastUpdatedByColumn: GridColDef<BrandCampaignItem> = {
    ...baseColumnProps,
    field: 'lastUpdatedBy',
    minWidth: 250,
    flex: 1,
    headerName: t('campaign.columns.lastUpdatedBy'),
    renderCell: ({ row }) => (
      <Typography variant="subtitle2" component="span">
        {row.updatedBy?.fullName() ?? '-'}
      </Typography>
    ),
  };

  const campaignSecondaryDiscountPercentage: GridColDef<BrandCampaignItem> = {
    ...baseColumnProps,
    width: 130,
    field: 'campaignSecondaryDiscountPercentage',
    headerName: t('campaign.columns.secondaryDiscountPercentage'),
    renderCell: ({ row }) =>
      row.secondaryDiscountPercentage ? `${row.secondaryDiscountPercentage} %` : '-',
  };
  const campaignTertiaryDiscountPercentage: GridColDef<BrandCampaignItem> = {
    ...baseColumnProps,
    width: 130,
    field: 'campaignTertiaryDiscountPercentage',
    headerName: t('campaign.columns.tertieryDiscountPercentage'),
    renderCell: ({ row }) =>
      row.tertiaryDiscountPercentage ? `${row.tertiaryDiscountPercentage} %` : '-',
  };

  return {
    campaignSecondaryDiscountPercentage,
    campaignTertiaryDiscountPercentage,
    isImposedColumn,
    lastUpdatedColumn,
    lastUpdatedByColumn,
  };
};
